import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { LayoutDashboard, ScanLine, FileText, Users, LogOut, UtensilsCrossed, Menu, Settings } from "lucide-react";

const nav = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/scanner", label: "Scanner un ticket", icon: ScanLine, testid: "nav-scan" },
  { to: "/factures", label: "Factures", icon: FileText, testid: "nav-invoices" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/parametres", label: "Paramètres", icon: Settings, testid: "nav-parametres" },
];

const NavContent = ({ onNavigate, idPrefix = "" }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="h-full flex flex-col bg-[#5E2129] text-[#FDFBF7]">
      <div className="px-7 py-8 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#C28E3A]/20 flex items-center justify-center">
            <UtensilsCrossed className="w-6 h-6 text-[#C28E3A]" />
          </div>
          <div>
            <h1 className="font-serif text-xl leading-none">DuPeyrou</h1>
            <p className="text-xs tracking-[0.2em] uppercase text-[#C28E3A] mt-1">Facturation</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
        {nav.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`${idPrefix}${n.testid}`}
              onClick={onNavigate}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200 ${
                  isActive ? "bg-[#FDFBF7] text-[#5E2129]" : "text-[#FDFBF7]/75 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <Icon className="w-5 h-5" />
              {n.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-4 py-5 border-t border-white/10">
        <div className="px-4 py-2 mb-2">
          <p className="text-sm font-semibold truncate">{user?.name || "Utilisateur"}</p>
          <p className="text-xs text-[#FDFBF7]/60 truncate">{user?.email}</p>
        </div>
        <button
          data-testid={`${idPrefix}logout-btn`}
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-[#FDFBF7]/75 hover:bg-white/10 hover:text-white transition-colors duration-200"
        >
          <LogOut className="w-5 h-5" />
          Déconnexion
        </button>
      </div>
    </div>
  );
};

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Desktop sidebar */}
      <aside className="hidden md:block w-72 fixed inset-y-0 left-0 z-30 no-print">
        <NavContent />
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-16 bg-[#5E2129] text-[#FDFBF7] no-print">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#C28E3A]/20 flex items-center justify-center">
            <UtensilsCrossed className="w-5 h-5 text-[#C28E3A]" />
          </div>
          <span className="font-serif text-lg">DuPeyrou</span>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-btn" className="p-2 rounded-lg hover:bg-white/10 transition-colors">
              <Menu className="w-6 h-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 border-0 [&>button]:text-white [&>button]:z-50">
            <SheetTitle className="sr-only">Menu de navigation</SheetTitle>
            <NavContent onNavigate={() => setOpen(false)} idPrefix="mobile-" />
          </SheetContent>
        </Sheet>
      </header>

      <main className="md:ml-72 min-h-screen">{children}</main>
    </div>
  );
}
